export const instagramUrl = 'https://www.instagram.com/bright_digital_hubonline/'
export const checkoutUrl = 'https://complete-senior-balance-mobility-bundle.impultienda.ar/checkout'
export const offerAnchor = '#offer'
export const contactEmail = 'compradigitalyaa@gmail.com'

export const productName = 'Complete Senior Balance & Mobility Bundle'

export const mockupUrl =
  'https://cdn.magicpatterns.com/uploads/7en3dqupyKDPNktjqKHo5D/Paquete_clinico_de_equilibrio_y_movilidad.png'

export const bodyAreas = [
  'Postural Control',
  'Gait Retraining',
  'Functional Rehabilitation',
  'Reactive Balance',
  'Complete Treatment Sessions',
]

export const organizedNeeds = [
  { question: 'Need advanced postural control techniques?', answer: "They're organized." },
  {
    question: 'Working with gait asymmetry or reduced foot clearance?',
    answer: 'Find specific retraining protocols.',
  },
  {
    question: 'Treating difficulties with chair rises, transfers, or stairs?',
    answer: 'Access structured functional rehabilitation exercises.',
  },
  {
    question: 'Need specialized reactive balance interventions?',
    answer: 'Explore clinician-supervised techniques with appropriate safety guidance.',
  },
  {
    question: 'Need a complete 20-, 30-, or 40-minute treatment session?',
    answer: 'Find ready-to-adapt examples.',
  },
]

export const systemFlow = ['Open the system', 'Select the protocol', 'Follow the sequence', 'Adapt to your patient']

export const showcaseFeatures = [
  'Advanced rehabilitation techniques',
  'Step-by-step therapeutic exercises',
  'Sets, repetitions, and rest periods',
  'Patient positioning and therapist assistance',
  'Exercise regressions and progressions',
  'Complete clinical treatment sessions',
  'Structured multiweek programs',
  'Practical assessment and documentation tools',
]

export type Volume = {
  number: string
  name: string
  intro: string
  itemsLabel?: string
  items?: string[]
  details: string
  note?: string
  tagline: string
}

export const volumes: Volume[] = [
  {
    number: '01',
    name: 'Advanced Postural Control & Sensory Integration',
    intro: 'Access specialized techniques for postural stability, controlled movement, and sensory integration.',
    details:
      'Includes practical exercise instructions, patient positioning, therapist guarding, task modifications, and structured clinical protocols. Organize treatment around specific postural control limitations.',
    tagline: 'Position. Control. Adapt.',
  },
  {
    number: '02',
    name: 'Advanced Gait Retraining & Locomotor Rehabilitation',
    intro: 'Find structured gait rehabilitation techniques for:',
    items: [
      'Foot clearance and foot placement',
      'Step-length control and walking symmetry',
      'Loading response and propulsion',
      'Planned obstacle negotiation',
      'Locomotor adaptability',
      'External gait cueing',
      'Body-weight-supported walking',
    ],
    details: 'Includes step-by-step instructions, assistance guidance, exercise dosage, and clinical progressions.',
    tagline: 'Observe. Retrain. Progress.',
  },
  {
    number: '03',
    name: 'Neuromuscular & Functional Rehabilitation',
    intro: 'Practical rehabilitation exercises for improving functional movement performance.',
    itemsLabel: 'Includes:',
    items: [
      'Functional muscle power',
      'Eccentric movement control',
      'Sit-to-stand retraining',
      'Bed mobility and transfers',
      'Step and stair rehabilitation',
      'Progressive functional resistance',
    ],
    details:
      'Every exercise includes positioning, sets, repetitions, rest periods, corrective cues, and modifications.',
    tagline: 'Strengthen. Coordinate. Restore Function.',
  },
  {
    number: '04',
    name: 'Reactive Balance & Motor Adaptation',
    intro: 'Specialized clinical techniques for balance recovery and adaptive movement responses.',
    itemsLabel: 'Includes:',
    items: [
      'Controlled perturbation-based training',
      'Compensatory stepping',
      'Multidirectional recovery responses',
      'Reactive postural strategies',
      'Motor adaptation',
      'Dual-task balance integration',
    ],
    details: 'Includes clinical selection, guarding requirements, dosage, and progression guidance.',
    note: 'Advanced reactive balance techniques require qualified supervision and appropriate fall protection.',
    tagline: 'Respond. Recover. Adapt.',
  },
  {
    number: '05',
    name: 'Advanced Clinical Rehabilitation Protocols',
    intro: 'Turn individual exercises into complete, organized rehabilitation sessions.',
    itemsLabel: 'Includes:',
    items: [
      'Postural control sessions',
      'Gait rehabilitation sessions',
      'Functional rehabilitation sessions',
      'Reactive balance sessions',
      'Integrated treatment plans',
      '20-, 30-, and 40-minute session examples',
      'Four-week and six-week rehabilitation programs',
      'Patient-specific clinical applications',
    ],
    details: "Select the treatment objective, follow the structured plan, and adapt it to your patient's needs.",
    tagline: 'Choose. Apply. Reassess.',
  },
]

export const sampleMeta = [
  ['Session focus', 'Functional rehabilitation'],
  ['Main goal', 'Sit-to-stand retraining and controlled chair descent'],
  ['Duration', '30 minutes'],
] as const

export const sampleTimeline = [
  ['Review patient status and session objective', '3 min'],
  ['Equipment preparation and positioning', '2 min'],
  ['Sit-to-stand motor retraining', '8 min'],
  ['Controlled chair descent practice', '7 min'],
  ['Functional movement training', '7 min'],
  ['Review and documentation', '3 min'],
] as const

export const protocolStructure = [
  'Exercise selection',
  'Positioning',
  'Sets',
  'Repetitions',
  'Rest',
  'Assistance',
  'Modifications',
]

export const differentiators = [
  {
    icon: 'activity',
    title: 'Structured Clinical Techniques',
    text: 'Find interventions organized around specific balance, gait, and functional rehabilitation needs.',
  },
  {
    icon: 'book',
    title: 'Clear Exercise Instructions',
    text: 'Access patient positioning, execution steps, sets, repetitions, rest periods, and modifications.',
  },
  {
    icon: 'calendar',
    title: 'Complete Treatment Plans',
    text: 'Use organized session examples and multiweek rehabilitation programs.',
  },
  {
    icon: 'clipboard',
    title: 'Practical Professional Tools',
    text: 'Access assessment resources, home exercise handouts, caregiver guides, and progress tracking forms.',
  },
  {
    icon: 'clock',
    title: 'Less Preparation Time',
    text: 'Keep your rehabilitation protocols and everyday clinical resources together in one organized collection.',
  },
] as const
